import type { FormInstance } from "../../../../../shared/frontend/ui";
import { App, Button } from "../../../../../shared/frontend/ui";
import React from "react";
import { useBlocker } from "react-router-dom";
import { spacing } from "../app/design-system";
import type { LocalizedText } from "../app/i18n/i18n";
import { useI18n } from "../app/i18n/useI18n";
import { CommunicationComposer,type CommunicationComposerProps } from "./CommunicationComposer";
import { TemplateEditorHeader } from "./TemplateEditorHeader";
import { EMPTY_JSON_OBJECT } from "../app/constants/jsonText";

const EDITOR_SETTLING_SNAPSHOT_DELAY_MS = 1200;
const DIRTY_EVALUATION_DEFER_MS = 80;
const TEMPLATE_EDITOR_ROWS = 30;

export type TemplateEditorSaveOptions = {
  closeAfterSave?: boolean;
};

export type TemplateEditorDirtySnapshot = Record<string, unknown>;

export type TemplateEditorDirtyController = {
  hasInitialSnapshot: () => boolean;
  isDirty: () => boolean;
  markEditorDirty: () => void;
  resetDirtyTracking: () => void;
  scheduleInitialSnapshotAfterEditorSettles: (bodyOverride?: string) => void;
  setInitialSnapshot: (bodyOverride?: string) => void;
  signalDirtyEvaluation: () => void;
};

export type TemplateEditorWorkspaceRef = TemplateEditorDirtyController & {
  closeSettings: () => void;
  openSettings: () => void;
  setSettingsOpen: (open: boolean) => void;
};

type TemplateEditorDirtyGuardOptions = {
  buildSnapshot: (bodyOverride?: string) => TemplateEditorDirtySnapshot;
  getCurrentBody: () => string;
};

export type TemplateEditorWorkspaceSettingsRenderProps = {
  closeSettings: () => void;
  dirty: TemplateEditorDirtyController;
  onValuesChange: NonNullable<CommunicationComposerProps["onValuesChange"]>;
  open: boolean;
  openSettings: () => void;
  setSettingsOpen: (open: boolean) => void;
};

/**
 * Was der Workspace einer eigenen Bearbeitungsfläche mitgibt: den Dirty-Wächter, damit sie
 * Änderungen melden kann, und den Zugang zum Einstellungs-Drawer.
 */
export type TemplateEditorWorkspaceBodyRenderProps = {
  dirty: TemplateEditorDirtyController;
  /** Der vollständige Workspace-Kopf, wenn er in die Fachkarte gehört. */
  header?: React.ReactNode;
  openSettings: () => void;
  /** Schließt über denselben Wächter wie der normale Zurück-Knopf. */
  requestClose: () => void;
};

export type TemplateEditorWorkspaceProps = {
  /** Beschriftung des Zurückwegs; Standard bleibt die Einstellungsnavigation der Vorlagenpflege. */
  backLabel?: LocalizedText;
  buildSnapshot: (bodyOverride?: string) => TemplateEditorDirtySnapshot;
  composerProps?: Partial<CommunicationComposerProps>;
  editorRef: React.RefObject<{ getValue?: () => string | null } | null>;
  /** Zusätzliche Schaltflächen im Kopf, links neben „speichern". */
  extraActions?: React.ReactNode;
  form: FormInstance;
  getCurrentBody?: () => string;
  /** Legt Kopf, Einstellungen und Speichern in die von der Fachfläche gerenderte Card. */
  headerInBody?: boolean;
  /** Zusätzliche Bedienelemente links neben den Kopfaktionen. */
  headerLeading?: React.ReactNode;
  /** Blendet den Standard-Zurückknopf aus, wenn die Fachfläche ihn an einer eigenen Stelle zeigt. */
  hideBackButton?: boolean;
  /** Der Fachbereich rendert den Titel als normalen Seitenkopf über seiner Card. */
  hideHeaderTitle?: boolean;
  mode: "edit" | "create";
  onEditorBodyChange?: (body: string) => void;
  onClose: () => void;
  onSave: (options?: TemplateEditorSaveOptions) => Promise<void>;
  open: boolean;
  /**
   * Eigene Bearbeitungsfläche an Stelle des `CommunicationComposer`. Kopf, Dirty-Wächter,
   * Navigationssperre, Speichern-Kette und Einstellungs-Drawer gelten unverändert — genau darum
   * bekommt der Systemdokument-Editor hier dieselbe Bedienung wie die Textvorlagen, statt sie
   * nachzubauen.
   */
  renderBody?: (props: TemplateEditorWorkspaceBodyRenderProps) => React.ReactNode;
  renderSettings?: (props: TemplateEditorWorkspaceSettingsRenderProps) => React.ReactNode;
  saving?: boolean;
  saveText?: LocalizedText;
  showSettings?: boolean;
  title: React.ReactNode;
  useComposerSettingsDrawer?: boolean;
};

function stableStringifyJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringifyJson).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([left], [right]) => left.localeCompare(right));
    return `{${entries.map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringifyJson(entryValue)}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

export function normalizeTemplateBodyForDirtyCheck(value: string | null | undefined): string {
  const body = String(value ?? "").trim();
  if (!body || body === EMPTY_JSON_OBJECT) return "";
  if (!body.startsWith("{") && !body.startsWith("[")) return body;
  try {
    return stableStringifyJson(JSON.parse(body));
  } catch {
    return body;
  }
}

export function normalizeStringArrayForDirtyCheck(value: unknown): string[] {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  return values.map((entry) => String(entry ?? "").trim()).filter(Boolean).sort();
}

export function normalizeNullableTextForDirtyCheck(value: unknown): string | null {
  const textValue = String(value ?? "").trim();
  return textValue ? textValue : null;
}

export function normalizeNullableIdForDirtyCheck(value: unknown): string | null {
  const id = String(value ?? "").trim();
  return id ? id : null;
}

export function snapshotsEqual(left: TemplateEditorDirtySnapshot, right: TemplateEditorDirtySnapshot): boolean {
  return stableStringifyJson(left) === stableStringifyJson(right);
}

function useTemplateEditorDirtyGuard(options: TemplateEditorDirtyGuardOptions): TemplateEditorDirtyController {
  const { buildSnapshot, getCurrentBody } = options;
  const [editorBodyDirty, setEditorBodyDirty] = React.useState(false);
  const [, setDirtyEvaluationVersion] = React.useState(0);
  const editorChangeVersionRef = React.useRef(0);
  const editorSettlingTimerRef = React.useRef<number | null>(null);
  const dirtyEvaluationTimerRef = React.useRef<number | null>(null);
  const initialSnapshotRef = React.useRef<TemplateEditorDirtySnapshot | null>(null);

  const setInitialSnapshot = React.useCallback((bodyOverride?: string) => {
    initialSnapshotRef.current = buildSnapshot(bodyOverride);
    setEditorBodyDirty(false);
  }, [buildSnapshot]);

  const scheduleInitialSnapshotAfterEditorSettles = React.useCallback((bodyOverride?: string) => {
    if (editorSettlingTimerRef.current != null) {
      window.clearTimeout(editorSettlingTimerRef.current);
    }
    const changeVersion = editorChangeVersionRef.current;
    editorSettlingTimerRef.current = window.setTimeout(() => {
      editorSettlingTimerRef.current = null;
      if (editorChangeVersionRef.current !== changeVersion) return;
      setInitialSnapshot(getCurrentBody() || bodyOverride);
    }, EDITOR_SETTLING_SNAPSHOT_DELAY_MS);
  }, [getCurrentBody, setInitialSnapshot]);

  const signalDirtyEvaluation = React.useCallback(() => {
    editorChangeVersionRef.current += 1;
    setDirtyEvaluationVersion((version) => version + 1);
    if (dirtyEvaluationTimerRef.current != null) {
      window.clearTimeout(dirtyEvaluationTimerRef.current);
    }
    dirtyEvaluationTimerRef.current = window.setTimeout(() => {
      dirtyEvaluationTimerRef.current = null;
      setDirtyEvaluationVersion((version) => version + 1);
    }, DIRTY_EVALUATION_DEFER_MS);
  }, []);

  const markEditorDirty = React.useCallback(() => {
    setEditorBodyDirty(true);
    signalDirtyEvaluation();
  }, [signalDirtyEvaluation]);

  const resetDirtyTracking = React.useCallback(() => {
    initialSnapshotRef.current = null;
    editorChangeVersionRef.current = 0;
    setEditorBodyDirty(false);
    if (editorSettlingTimerRef.current != null) {
      window.clearTimeout(editorSettlingTimerRef.current);
      editorSettlingTimerRef.current = null;
    }
    if (dirtyEvaluationTimerRef.current != null) {
      window.clearTimeout(dirtyEvaluationTimerRef.current);
      dirtyEvaluationTimerRef.current = null;
    }
  }, []);

  const hasInitialSnapshot = React.useCallback((): boolean => {
    return initialSnapshotRef.current != null;
  }, []);

  const isDirty = React.useCallback((): boolean => {
    const initialSnapshot = initialSnapshotRef.current;
    if (!initialSnapshot) return false;
    const currentSnapshot = buildSnapshot(String(initialSnapshot.body ?? ""));
    return editorBodyDirty || !snapshotsEqual(initialSnapshot, currentSnapshot);
  }, [buildSnapshot, editorBodyDirty]);

  React.useEffect(() => resetDirtyTracking, [resetDirtyTracking]);

  return React.useMemo(
    () => ({
      hasInitialSnapshot,
      isDirty,
      markEditorDirty,
      resetDirtyTracking,
      scheduleInitialSnapshotAfterEditorSettles,
      setInitialSnapshot,
      signalDirtyEvaluation
    }),
    [
      hasInitialSnapshot,
      isDirty,
      markEditorDirty,
      resetDirtyTracking,
      scheduleInitialSnapshotAfterEditorSettles,
      setInitialSnapshot,
      signalDirtyEvaluation
    ]
  );
}

export const TemplateEditorWorkspace = React.forwardRef<TemplateEditorWorkspaceRef, TemplateEditorWorkspaceProps>(function TemplateEditorWorkspace(
  props,
  ref
) {
  const {
    backLabel,
    buildSnapshot,
    composerProps,
    editorRef,
    extraActions,
    form,
    getCurrentBody: getCurrentBodyProp,
    headerInBody = false,
    headerLeading,
    hideBackButton = false,
    hideHeaderTitle = false,
    mode,
    onClose,
    onEditorBodyChange,
    onSave,
    open,
    renderBody,
    renderSettings,
    saveText,
    saving,
    showSettings,
    title,
    useComposerSettingsDrawer = true
  } = props;
  const { modal } = App.useApp();
  const { tr } = useI18n();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const savePromiseRef = React.useRef<Promise<void> | null>(null);

  const getCurrentBody = React.useCallback((): string => {
    if (getCurrentBodyProp) return getCurrentBodyProp();
    const editorValue = editorRef.current?.getValue?.();
    if (editorValue != null) return editorValue;
    const formBody = form.getFieldValue("body");
    return formBody == null ? "" : String(formBody);
  }, [editorRef, form, getCurrentBodyProp]);

  const dirty = useTemplateEditorDirtyGuard({ buildSnapshot, getCurrentBody });

  const openSettings = React.useCallback(() => setSettingsOpen(true), []);
  const closeSettings = React.useCallback(() => setSettingsOpen(false), []);

  React.useImperativeHandle(
    ref,
    () => ({
      ...dirty,
      closeSettings,
      openSettings,
      setSettingsOpen
    }),
    [closeSettings, dirty, openSettings]
  );

  const closeWithoutPrompt = React.useCallback(() => {
    dirty.resetDirtyTracking();
    closeSettings();
    onClose();
  }, [closeSettings, dirty, onClose]);

  const requestSave = React.useCallback(async (options?: TemplateEditorSaveOptions) => {
    const currentSave = savePromiseRef.current;
    if (currentSave) {
      await currentSave;
      if (options?.closeAfterSave) {
        closeWithoutPrompt();
      }
      return;
    }

    const savePromise = onSave(options);
    savePromiseRef.current = savePromise;
    try {
      await savePromise;
    } finally {
      if (savePromiseRef.current === savePromise) {
        savePromiseRef.current = null;
      }
    }
  }, [closeWithoutPrompt, onSave]);

  /**
   * Der Wächter für ungespeicherte Änderungen — mit **drei** Wegen.
   *
   * Zwei genügen nicht: Wer den Dialog wegklickt oder Escape drückt, will fast immer im Editor
   * bleiben; vorher galt genau das als „verwerfen" und die Arbeit war weg. Abbrechen bleibt jetzt
   * folgenlos, verwerfen ist eine ausdrückliche Entscheidung.
   */
  const askUnsavedChanges = React.useCallback(
    () => new Promise<"save" | "discard" | "stay">((resolve) => {
      let instance: { destroy: () => void } | null = null;
      const decide = (answer: "save" | "discard" | "stay") => {
        instance?.destroy();
        resolve(answer);
      };

      // ui-standard-exception: modal.confirm - navigation guard needs Save/Discard/Stay branching.
      instance = modal.confirm({
        title: tr("Unsaved changes"),
        content: tr("Save or discard changes?"),
        onCancel: () => resolve("stay"),
        footer: () => (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.sm, marginTop: spacing.md }}>
            <Button onClick={() => decide("stay")}>{tr("Cancel")}</Button>
            <Button danger onClick={() => decide("discard")}>{tr("Discard")}</Button>
            <Button type="primary" onClick={() => decide("save")}>{tr("Save")}</Button>
          </div>
        ),
      });
    }),
    [modal, tr]
  );

  const requestClose = React.useCallback(() => {
    if (savePromiseRef.current) {
      requestSave({ closeAfterSave: true }).catch(() => {});
      return;
    }

    if (!dirty.hasInitialSnapshot() || !dirty.isDirty()) {
      closeWithoutPrompt();
      return;
    }
    void askUnsavedChanges().then((answer) => {
      if (answer === "save") return requestSave({ closeAfterSave: true }).catch(() => {});
      if (answer === "discard") closeWithoutPrompt();
      return undefined;
    });
  }, [askUnsavedChanges, closeWithoutPrompt, dirty, requestSave]);

  const blocker = useBlocker(open && dirty.isDirty());
  const prevBlockerStateRef = React.useRef(blocker.state);

  React.useEffect(() => {
    if (blocker.state === "blocked" && prevBlockerStateRef.current !== "blocked") {
      prevBlockerStateRef.current = "blocked";
      if (savePromiseRef.current) {
        requestSave().then(() => blocker.proceed()).catch(() => {});
        return;
      }

      void askUnsavedChanges().then((answer) => {
        if (answer === "save") {
          return requestSave({ closeAfterSave: true }).then(() => blocker.proceed()).catch(() => {});
        }

        if (answer === "discard") {
          closeWithoutPrompt();
          blocker.proceed();
          return undefined;
        }

        // Im Editor bleiben: Der angehaltene Wechsel wird zurückgenommen, nicht fortgesetzt.
        blocker.reset?.();
        prevBlockerStateRef.current = "unblocked";
        return undefined;
      });
    }
    prevBlockerStateRef.current = blocker.state;
  }, [askUnsavedChanges, blocker, blocker.state, closeWithoutPrompt, requestSave]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (event: BeforeUnloadEvent) => {
      if (dirty.isDirty()) event.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, open]);

  React.useEffect(() => {
    if (!open) {
      dirty.resetDirtyTracking();
      closeSettings();
    }
  }, [closeSettings, dirty, open]);

  const handleEditorChange = React.useCallback((body?: string) => {
    const next = String(body ?? "");
    form.setFieldValue("body", next);
    onEditorBodyChange?.(next);
    dirty.markEditorDirty();
    composerProps?.onEditorChange?.(next);
  }, [composerProps, dirty, form, onEditorBodyChange]);

  const handleEditorInteraction = React.useCallback(() => {
    dirty.markEditorDirty();
    composerProps?.onEditorInteraction?.();
  }, [composerProps, dirty]);

  const handleValuesChange = React.useCallback<NonNullable<CommunicationComposerProps["onValuesChange"]>>((changedValues, allValues) => {
    dirty.signalDirtyEvaluation();
    composerProps?.onValuesChange?.(changedValues, allValues);
  }, [composerProps, dirty]);

  if (!open) return null;

  const header = (
    <TemplateEditorHeader
      title={title}
      backLabel={backLabel}
      saving={saving}
      onBack={requestClose}
      onOpenSettings={openSettings}
      onSave={() => requestSave().catch(() => {})}
      saveText={saveText}
      extraActions={extraActions}
      showSettings={showSettings}
      embedded={headerInBody}
      hideBackButton={hideBackButton}
      hideTitle={hideHeaderTitle}
      leading={headerLeading}
    />
  );

  return (
    <div>
      {headerInBody ? null : header}

      {renderBody ? (
        /* `dirty` ist ein Bündel von Callbacks (isDirty, markEditorDirty, …); die Refs darin werden
           erst beim Aufruf gelesen, nicht beim Übergeben. Die Regel sieht nur, dass ein ref-lesender
           Wert im Render weitergereicht wird. Auflösen ließe sich das nur, indem der renderBody-
           Vertrag aller Aufrufer auf Einzelwerte umgestellt wird — dann wäre der Dirty-Zustand ein
           Render-Wert und die Bearbeitungsfläche würde bei jedem Tastendruck neu rendern. */
        // eslint-disable-next-line react-hooks/refs
        renderBody({ dirty, header: headerInBody ? header : undefined, openSettings, requestClose })
      ) : (
        <CommunicationComposer
          {...composerProps}
          form={form}
          mode={mode}
          editorRows={TEMPLATE_EDITOR_ROWS}
          drawerOpen={useComposerSettingsDrawer ? settingsOpen : false}
          onDrawerClose={useComposerSettingsDrawer ? closeSettings : undefined}
          onEditorChange={handleEditorChange}
          onEditorInteraction={handleEditorInteraction}
          onValuesChange={handleValuesChange}
        />
      )}

      {renderSettings?.({
        closeSettings,
        dirty,
        onValuesChange: handleValuesChange,
        open: settingsOpen,
        openSettings,
        setSettingsOpen
      })}
    </div>
  );
});
